import {
	GATE_ENVELOPE_SCHEMA,
	SET_FEATURE_GATE_ENVELOPE_MESSAGE_TYPE,
	validateGateEnvelope,
	type GateCustomAttributes,
	type GateEnvelope,
	type GateIdentifiers,
	type ProviderValues,
	type SetFeatureGateEnvelopeMessage,
} from '../contract/envelope';
import { SET_FEATURE_GATE_OVERRIDES_MESSAGE_TYPE, type GateOverrides } from '../contract/overrides';
import { computeProfileHash, type GateProfile } from '../contract/profile-hash';
import type { Registry } from '../contract/registry';

import { sanitiseExperimentValues } from './sanitise-values';

/** The identity the producer is currently evaluating for. `targetApp` comes from the publisher options. */
export type GateProfileInput = Omit<GateProfile, 'targetApp'>;

export interface GateEnvelopePublisherOptions {
	readonly targetApp: string;
	/** Reads the raw values blob. In the frontend this is `pollingProvider.getExperimentValues`. */
	readonly getValues: () => Promise<ProviderValues>;
	/** Hands a validated envelope to the extension page. See {@link postGateEnvelopeToExtensionPage}. */
	readonly post: (message: SetFeatureGateEnvelopeMessage) => void;
	readonly onError: (error: Error, impact: string) => void;
	/**
	 * Which registry decides what gets published. Defaults to the shipped one; tests inject theirs.
	 * See `sanitiseExperimentValues` for what this filtering costs as well as what it saves.
	 */
	readonly registry?: Registry;
}

export type GateEnvelopePublisher = (
	profile: GateProfileInput,
	update?: ProviderValues,
	now?: number,
) => Promise<GateEnvelope | undefined>;

/**
 * Creates the producer of chrome-layer gate values. Runs only while a Rovo surface is open, which is
 * what keeps the engagement rule true by construction. `clientSdkKey` is stripped, and the envelope
 * goes through the receiver's own validator so a producer bug fails here rather than in storage.
 *
 * Two constraints nothing but this comment enforces:
 *
 * - The provider must not implement `setApplyUpdateCallback`. The SDK would then apply updates
 *   itself mid-poll, and one landing between reading the values and publishing would ship a payload
 *   no single evaluation produced. This publisher reads a settled provider result instead.
 * - A publish with no identifier at all is skipped, because the one storage slot per extension
 *   profile would otherwise take an identity-less envelope over a signed-in user's.
 */
export function createGateEnvelopePublisher(
	options: GateEnvelopePublisherOptions,
): GateEnvelopePublisher {
	// Never behind wall-clock time, so a fresh instance cannot publish a generation older than what a
	// previous one stored. Closure-scoped, so nothing outside can advance it.
	let lastGeneration = 0;

	const nextGeneration = (now: number): number => {
		lastGeneration = Math.max(lastGeneration + 1, now);
		return lastGeneration;
	};

	return async (profile, update, now = Date.now()) => {
		try {
			if (Object.values(profile.identifiers).every((value) => value === undefined)) {
				return undefined;
			}

			const values = update ?? (await options.getValues());
			const identifiers = compact(profile.identifiers) as GateIdentifiers;
			const customAttributes = compact(profile.customAttributes) as GateCustomAttributes;
			const targetApp = options.targetApp;

			// Identity is hashed into `profileHash` and then dropped; nothing downstream reads one.
			const sanitised = await sanitiseExperimentValues(values.experimentValues, options.registry);
			if (sanitised.invalidInput) {
				// Refuse rather than publish an empty payload, which the receiver would take as current
				// truth until the next good poll.
				options.onError(
					new Error('Feature gates: refusing to publish envelope (missing-payload)'),
					'Chrome-layer gates keep their previous values',
				);
				return undefined;
			}
			if (sanitised.unsupportedHash !== undefined) {
				options.onError(
					new Error(
						`Feature gates: unrecognised hash_used "${sanitised.unsupportedHash}", publishing no values`,
					),
					'Chrome-layer gates fall back to their registry defaults',
				);
			}

			const envelope: GateEnvelope = {
				schema: GATE_ENVELOPE_SCHEMA,
				generation: nextGeneration(now),
				fetchedAt: now,
				profileHash: await computeProfileHash({ targetApp, identifiers, customAttributes }),
				targetApp,
				payload: { experimentValues: sanitised.values },
			};

			const validation = validateGateEnvelope(envelope);
			if (!validation.ok) {
				options.onError(
					new Error(`Feature gates: refusing to publish envelope (${validation.reason})`),
					'Chrome-layer gates keep their previous values',
				);
				return undefined;
			}

			options.post({ type: SET_FEATURE_GATE_ENVELOPE_MESSAGE_TYPE, value: envelope });
			return envelope;
		} catch (error) {
			options.onError(
				error instanceof Error ? error : new Error(String(error)),
				'Feature gates: envelope not published',
			);
			return undefined;
		}
	};
}

/**
 * Transport for a producer running in an iframe inside an extension page. Never route it through a
 * helper that falls back to `'*'`, which would hand the values to whatever framed us. A producer not
 * hosted in an extension page has nobody to tell and does nothing.
 */
export function postGateEnvelopeToExtensionPage(
	message: SetFeatureGateEnvelopeMessage,
	extensionOrigin: string | undefined,
): void {
	if (typeof window === 'undefined' || window.parent === window) {
		return;
	}
	window.parent.postMessage(message, requireExtensionOrigin(extensionOrigin));
}

/** Same transport, for dev/QA overrides. See the frontend's `syncGateOverridesToExtension`. */
export function postGateOverridesToExtensionPage(
	overrides: GateOverrides,
	extensionOrigin: string | undefined,
): void {
	if (typeof window === 'undefined' || window.parent === window) {
		return;
	}
	window.parent.postMessage(
		{ type: SET_FEATURE_GATE_OVERRIDES_MESSAGE_TYPE, overrides },
		requireExtensionOrigin(extensionOrigin),
	);
}

/**
 * The configured target origin, refused unless it is a bare `chrome-extension://<id>`.
 *
 * `postMessage` fails two different ways here: an unparseable target throws `SyntaxError`, but a
 * well-formed origin that is not the parent's drops the message with no error anywhere. The silent
 * one is the problem, since a mistyped `EXTENSION_URL` would present as the chrome layer resolving
 * registry defaults forever with nothing to explain it. Checked up front so it is loud instead.
 *
 * A single shape rather than an allowlist of ids: the value is baked in at build time from this
 * repo's own id map, so an allowlist would be a second copy of that map to keep in step, and it
 * would still not catch a typo within an allowed entry.
 */
function requireExtensionOrigin(extensionOrigin: string | undefined): string {
	if (!extensionOrigin || extensionOrigin === '*') {
		throw new Error('Feature gates: extension origin is not configured');
	}

	let parsed: URL;
	try {
		parsed = new URL(extensionOrigin);
	} catch {
		throw new Error(`Feature gates: extension origin is not a URL (${extensionOrigin})`);
	}

	// `URL.origin` is unusable here: `chrome-extension:` is not a special scheme, so it is `'null'`
	// in jsdom and Node while Chrome reports the real origin. Compared field by field instead.
	const isBareExtensionOrigin =
		parsed.protocol === 'chrome-extension:' &&
		parsed.host !== '' &&
		(parsed.pathname === '' || parsed.pathname === '/') &&
		parsed.search === '' &&
		parsed.hash === '' &&
		parsed.username === '' &&
		parsed.password === '';
	if (!isBareExtensionOrigin) {
		throw new Error(
			`Feature gates: refusing to post to a non-extension origin (${extensionOrigin})`,
		);
	}

	return extensionOrigin;
}

/** Drops `undefined` entries so the envelope survives structured cloning and validation unchanged. */
function compact<T extends object>(value: T): Partial<T> {
	const out: Record<string, unknown> = {};
	for (const [key, entry] of Object.entries(value)) {
		if (entry !== undefined) {
			out[key] = entry;
		}
	}
	return out as Partial<T>;
}
